/**
 * Rebuild the three hierarchy documents from Postgres rows.
 *
 * Firestore stored the chart's SHAPE as three nested documents, and 40-odd
 * components read them in that shape. Postgres stores the same information
 * relationally — `account_groups` rows plus `accounts.group_id` /
 * `accounts.parent_account_id`. Something has to bridge that, and doing it here
 * means the components do not each learn a second shape.
 *
 * This is the exact INVERSE of the transformer's `buildAccountGroups`
 * (migration/src/map/groups.js), which is what makes it checkable rather than
 * guessed: that module flattens the documents into rows, this one nests the
 * rows back.
 *
 * ===========================================================================
 * EVERY SHAPE BELOW WAS MEASURED, NOT INFERRED
 * ===========================================================================
 *
 * The first version of this module was written from the transformer's prose and
 * got three things wrong, all of which would have rendered as missing or empty
 * sections in the chart of accounts. supabase/tests/refdata-parity.sh caught
 * them by comparing live Firestore dev against the loaded estate. The documents
 * as they actually exist (dive-dev-f9d3f, 2026-09-05, uniform across all six
 * orgs that have a chart):
 *
 *   accountsHierarchy  { types: [ { type, groups: [ { groupName,
 *                          accounts: [ { id, subAccounts: [id] } ] } ] } ] }
 *   fundsHierarchy     { groups: [ { groupName,
 *                          funds: [ { id, subFunds: [id] } ] } ] }
 *   labelsHierarchy    { groups: [ { groupName,
 *                          labels: [ { id, subLabels: [id] } ] } ] }
 *
 * Note what is NOT there: a group carries EXACTLY two keys, `groupName` and its
 * items array. There is no `isSystem` flag and no group-level `subAccounts`
 * list — children hang off their parent item, not off the group. And each side
 * names its items differently: `accounts`/`subAccounts`, `funds`/`subFunds`,
 * `labels`/`subLabels`. Emitting the account vocabulary on the fund side hands
 * every fund-group consumer an undefined array.
 *
 * ===========================================================================
 * THE EQUITY SPLIT — the part that reads as a contradiction until you look
 * ===========================================================================
 *
 * Funds and accounts are ONE table here (the F-merge): a Firestore fund and its
 * mirror equity account become a single row, and `v_funds` is the fund-side
 * projection of it. Riverbend, for example, has 48 Firestore accounts and 10
 * Firestore funds, and 48 Postgres rows — 38 ordinary accounts plus 10 merged
 * fund rows. Nothing was lost; ten pairs became ten rows.
 *
 * That merge is why Equity groups are split across BOTH documents, and the
 * discriminator is `is_system`:
 *
 *   is_system = true   ONE group per org, always named "Fund-Linked Accounts"
 *                      (migration/src/map/groups.js:43 sets the flag from that
 *                      exact name). It is the Equity type appended to
 *                      accountsHierarchy. MEASURED: its `accounts` array holds
 *                      the fund ids themselves — fund-general, fund-thrift, …
 *                      — in fundsHierarchy order. So the Equity section of the
 *                      chart of accounts is precisely "every fund, flat", and
 *                      it is rebuilt from the funds rather than from any row
 *                      pointing at that group (in Postgres none does: the
 *                      merged row takes the fundsHierarchy placement, because
 *                      "the fund side wins on every F-merge conflict",
 *                      migration/src/map/groups.js:24).
 *
 *   is_system = false  The real fund groups — Operating Funds, Restricted
 *                      Funds, Grants, Uncategorized. These are fundsHierarchy,
 *                      and they never appear in accountsHierarchy.
 *
 * Sending all Equity groups to fundsHierarchy (the first version) both invented
 * fund groups that do not exist and deleted the chart's Equity section.
 *
 * A group's identity is its NAME, not its id: `groupId()` hashes
 * (org, type, name), so a rename is a different group. Reconstructing by name
 * is therefore correct, not a shortcut.
 */

/**
 * The order the types appear in the document. Equity is LAST because the
 * transformer appends it (migration/src/map/groups.js:19-21) and the live
 * documents agree — Assets, Liabilities, Income, Expenses, Equity.
 */
const ACCOUNT_TYPE_ORDER = ["Assets", "Liabilities", "Income", "Expenses", "Equity"];

/** The one group name the transformer flags as system. */
const SYSTEM_EQUITY_GROUP = "Fund-Linked Accounts";

/** Rows carry different name columns per projection; sort_order leads either way. */
const nameOf = (r) => r.name ?? r.account_name ?? r.fund_name ?? r.label_name ?? "";

/**
 * The name tie-break is PINNED TO "en", deliberately.
 *
 * `sort_order` is `NOT NULL DEFAULT 0`, so ties are the normal case for freshly
 * loaded rows and the name comparison decides real chart order. Bare
 * `localeCompare` uses the runtime's default locale: supabase/tests/hierarchy-parity.mjs
 * proves this module byte-exact under Node on an en dev box, while the app runs
 * in whatever locale the customer's browser is set to — and `tr` (dotless i) or
 * `sv` (z < ä) order the same names differently. The gate would stay green while
 * a customer's chart of accounts rendered in an order it never proved.
 *
 * "en" with an explicit sensitivity is what the gate measured, stated rather
 * than inherited. Collation has already cost this repo one incident on the
 * server side (fix/schema-collation-order); this is the same mistake's client half.
 */
const byName = (a, b) =>
  String(nameOf(a)).localeCompare(String(nameOf(b)), "en", { numeric: false, sensitivity: "variant" });
const bySort = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || byName(a, b);

/**
 * One group document: `{ groupName, <itemsKey>: [ { id, <childKey>: [id] } ] }`.
 *
 * @param {object} group        the account_groups / label_groups row
 * @param {Array}  items        rows belonging to this group (parents and children)
 * @param {string} itemsKey     "accounts" | "funds" | "labels"
 * @param {string} childKey     "subAccounts" | "subFunds" | "subLabels"
 * @param {string} parentField  the row's self-reference column
 */
function groupDoc(group, items, itemsKey, childKey, parentField) {
  const children = new Map();
  const tops = [];
  for (const row of items) {
    const parent = row[parentField];
    if (parent) {
      if (!children.has(parent)) children.set(parent, []);
      children.get(parent).push(row);
    } else {
      tops.push(row);
    }
  }
  return {
    groupName: group.name,
    [itemsKey]: tops.sort(bySort).map((row) => ({
      id: row.id,
      [childKey]: (children.get(row.id) ?? []).sort(bySort).map((c) => c.id),
    })),
  };
}

/** Index rows by their group, so each group is filled in one pass. */
function byGroup(rows) {
  const m = new Map();
  for (const r of rows) {
    if (!r.group_id) continue;
    if (!m.has(r.group_id)) m.set(r.group_id, []);
    m.get(r.group_id).push(r);
  }
  return m;
}

/**
 * @param {Array} groups   `account_groups` rows
 * @param {Array} accounts `accounts` rows (group_id, parent_account_id)
 * @param {Array} funds    `v_funds` rows (group_id, parent_fund_id) — the
 *                         fund-side projection of the same table
 * @returns {{accountsHierarchy: object, fundsHierarchy: object}}
 */
export function buildHierarchies(groups = [], accounts = [], funds = []) {
  const accountsIn = byGroup(accounts);
  const fundsIn = byGroup(funds);

  const isSystemEquity = (g) =>
    g.acct_type === "Equity" && (g.is_system === true || g.name === SYSTEM_EQUITY_GROUP);

  const fundGroups = groups.filter((g) => g.acct_type === "Equity" && !isSystemEquity(g)).sort(bySort);
  const systemEquity = groups.find(isSystemEquity) ?? null;
  const chartGroups = groups.filter((g) => g.acct_type !== "Equity");

  const fundsHierarchy = {
    groups: fundGroups.map((g) => groupDoc(g, fundsIn.get(g.id) ?? [], "funds", "subFunds", "parent_fund_id")),
  };

  const byType = new Map();
  for (const g of chartGroups) {
    if (!byType.has(g.acct_type)) byType.set(g.acct_type, []);
    byType.get(g.acct_type).push(g);
  }

  // Known types in the document's own order, then anything unexpected
  // alphabetically — carried rather than dropped, because silently losing a
  // type hides accounts from the chart entirely, which is invisible until
  // someone notices money missing from a screen.
  const typeNames = [
    ...ACCOUNT_TYPE_ORDER.filter((t) => byType.has(t)),
    ...[...byType.keys()].filter((t) => !ACCOUNT_TYPE_ORDER.includes(t)).sort(),
  ];

  const types = typeNames.map((type) => ({
    type,
    groups: byType.get(type).sort(bySort).map((g) =>
      groupDoc(g, accountsIn.get(g.id) ?? [], "accounts", "subAccounts", "parent_account_id"),
    ),
  }));

  // ORDER IS NOT PRESERVED HERE, and this is the one place in the module where
  // that is true. The mirror's order in Firestore is historical and arbitrary:
  // on Riverbend it happens to be group-major, on Ramp the single Restricted
  // fund sits second, ahead of twelve Unrestricted ones. Postgres cannot
  // reproduce it because the F-merged row keeps the FUND placement and the
  // accountsHierarchy placement is discarded at load (migration/src/map/groups.js:24),
  // so there is no column to sort by. Membership is exact; order is
  // deterministic and group-major, which is defensible on its own terms. If the
  // historical order ever turns out to matter, it has to be carried across by
  // the transformer — it cannot be recovered here.
  //
  // The appended Equity type: the system group, holding every fund FLAT.
  //
  // Measured on Riverbend, whose Grants group nests three sub-funds under
  // fund-grants: fundsHierarchy shows that nesting, and the Equity mirror lists
  // all ten funds at the top level with an empty subAccounts each, ordered
  // parent-then-children. So it is a depth-first flatten of fundsHierarchy, not
  // a copy of its top level — taking only the tops silently drops every
  // sub-fund from the chart's Equity section (7 of 10 rows, on that org).
  if (systemEquity) {
    types.push({
      type: "Equity",
      groups: [{
        groupName: systemEquity.name,
        accounts: fundsHierarchy.groups.flatMap((g) =>
          g.funds.flatMap((f) => [
            { id: f.id, subAccounts: [] },
            ...f.subFunds.map((id) => ({ id, subAccounts: [] })),
          ]),
        ),
      }],
    });
  }

  return { accountsHierarchy: { types }, fundsHierarchy };
}

/**
 * `labelsHierarchy` from `label_groups` rows plus labels. Same group shape as
 * the other two, under its own vocabulary.
 */
export function buildLabelsHierarchy(labelGroups = [], labels = []) {
  const inGroup = byGroup(labels);
  return {
    groups: [...labelGroups]
      .sort(bySort)
      .map((g) => groupDoc(g, inGroup.get(g.id) ?? [], "labels", "subLabels", "parent_label_id")),
  };
}
